import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Box, CheckCircle2, Eye, Palette, Layers } from 'lucide-react';
import { cgiServiceSchema, breadcrumbSchema } from '../utils/structuredData';

const CGIStagingPage = () => {
  const features = [
    'Photorealistic CGI visualization',
    'Virtual staging for property marketing',
    'Heritage building visualization',
    '3D rendering and animation',
    'Commercial fit-out previews',
    'Interior design visualization'
  ];

  const capabilities = [
    {
      icon: Eye,
      title: 'Photorealistic Quality',
      description: 'Cinema-quality CGI that brings designs to life'
    },
    {
      icon: Palette,
      title: 'Design Flexibility',
      description: 'Explore multiple design options quickly'
    },
    {
      icon: Layers,
      title: 'Full Integration',
      description: 'Seamlessly integrated with scan data'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="CGI Visualization & Virtual Staging | Photorealistic 3D Rendering"
        description="Photorealistic CGI visualisation and virtual staging for property marketing and commercial fit-outs. 3D rendering across the UK. Get a free quote."
        keywords="CGI visualization, virtual staging, photorealistic rendering, property CGI, 3D rendering London, commercial fit-out CGI, heritage building visualization, interior design CGI, property marketing visuals"
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/cgi` : ''}
        structuredData={[
          cgiServiceSchema,
          breadcrumbSchema([
            { name: 'Home', url: typeof window !== 'undefined' ? window.location.origin : '' },
            { name: 'CGI & Staging', url: typeof window !== 'undefined' ? `${window.location.origin}/cgi` : '' }
          ])
        ]}
      />
      <Navbar scrolled={true} />

      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-pink-50 rounded-full mb-6">
              <Box className="h-6 w-6 text-pink-600" />
              <span className="text-pink-600 font-semibold">CGI STAGING & VISUALIZATION</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Photorealistic CGI & Visualization
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform spaces with stunning CGI for commercial fit-outs and heritage projects.
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-16">
            <img
              src="https://images.unsplash.com/photo-1601002257790-ebe0966a85ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjB2aWxsYSUyMGludGVyaW9yfGVufDB8fHx8MTc3MjcxOTQyOHww&ixlib=rb-4.1.0&q=85&w=1400"
              alt="Photorealistic CGI visualisation of a luxury villa interior by Digital Scanning Group"
              className="w-full h-[500px] object-cover"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Bring Your Vision to Life
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  CGI staging and visualization transform empty spaces into fully furnished, designed environments—perfect for property marketing, investor presentations, and stakeholder engagement.
                </p>
                <p>
                  Our photorealistic CGI combines accuracy from 3D scan data with creative visualization to showcase the full potential of commercial properties, heritage buildings, and luxury developments.
                </p>
                <p>
                  Whether you're marketing a new development, planning a heritage restoration, or presenting design concepts to clients, our CGI services deliver stunning, persuasive visuals.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">CGI & Visualization Services</h3>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-pink-50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-pink-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
              <button className="mt-8 w-full px-8 py-4 bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-pink-500/50 transition-all duration-300">
                Request a Quote
              </button>
            </div>
          </div>

          {/* Capabilities Section */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our CGI Capabilities</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => {
                const Icon = capability.icon;
                return (
                  <div key={index} className="p-8 bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl text-center">
                    <div className="inline-flex p-4 bg-white rounded-xl mb-4 shadow-lg">
                      <Icon className="h-8 w-8 text-pink-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{capability.title}</h3>
                    <p className="text-gray-600">{capability.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CGIStagingPage;
